import React from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import NotFound from "./NotFound";
import { useSEO } from "../lib/useSEO";
import { OHIO_CITIES, OHIO_INFO } from "../data/content";
import { ACCIDENTS } from "../data/accidents";
import CityGuide from "./CityGuide";
import { getCityGuide, hasCityGuide } from "../data/city-guides";
import { ArrowRight, MapPin, Building2, Scale, HelpCircle } from "lucide-react";

const SITE_URL = "https://instabridgefunding.com";

// Per-city CTR-optimized SEO metadata.
// Query synonyms embedded: "pre-settlement funding" + "lawsuit loans" + "settlement advance".
// Cleveland currently ranks #6 for "pre-settlement funding cleveland" (49 imp) and #12 for
// "legal funding cleveland" (38 imp) — the CTR levers below are designed to convert those
// existing impressions into clicks.
const CITY_SEO = {
  cleveland: {
    title: "Cleveland Pre-Settlement Funding & Lawsuit Loans | 24-Hour Approval",
    meta: "Cleveland plaintiffs: $500–$25,000 on your active injury lawsuit, funded in 24 hours. Non-recourse, no credit check. Attorney-founded across Cuyahoga County.",
  },
  columbus: {
    title: "Columbus Pre-Settlement Funding & Lawsuit Loans | 24-Hour Approval",
    meta: "Columbus plaintiffs: $500–$25,000 against your injury lawsuit, deposited within 24 hours of attorney verification. Non-recourse, no credit check. Franklin County.",
  },
  cincinnati: {
    title: "Cincinnati Pre-Settlement Funding & Lawsuit Loans | 24-Hour Cash",
    meta: "Cincinnati plaintiffs: $500–$25,000 pre-settlement funding on your active injury lawsuit, funded in 24 hours. Non-recourse, no credit check. Hamilton County served.",
  },
  toledo: {
    title: "Toledo Pre-Settlement Funding & Lawsuit Loans | Funded in 24 Hours",
    meta: "Toledo & Lucas County plaintiffs: $500–$25,000 in pre-settlement funding on your injury lawsuit, funded within 24 hours. Non-recourse, no credit check, attorney-founded.",
  },
  dayton: {
    title: "Dayton Pre-Settlement Funding & Lawsuit Loans | 24-Hour Approval",
    meta: "Dayton plaintiffs: $500–$25,000 pre-settlement cash on your active injury lawsuit, funded in 24 hours. Non-recourse, no credit check. Montgomery County served.",
  },
};

export default function CityDetail() {
  const { city: citySlug } = useParams();
  // Cities migrated to the flagship template (CityGuide component + state/city
  // data schema) render through the shared CityGuide. Everything else falls
  // back to the legacy CityTemplate below until we migrate it.
  if (hasCityGuide(citySlug)) {
    const guide = getCityGuide(citySlug);
    return <CityGuide city={guide.city} state={guide.state} />;
  }
  return <CityTemplate citySlug={citySlug} />;
}

function CityTemplate({ citySlug }) {
  const city = OHIO_CITIES.find((c) => c.slug === citySlug);
  const seo = city ? CITY_SEO[city.slug] : null;

  useSEO(
    seo?.title || (city ? `${city.name}, Ohio pre-settlement funding` : "City not found"),
    seo?.meta || (city ? `Pre-settlement funding for ${city.name} plaintiffs.` : ""),
    city
      ? {
          canonical: `${SITE_URL}/states/ohio/${city.slug}`,
          suffix: false,
          jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Service",
                name: `Pre-Settlement Funding in ${city.name}, Ohio`,
                provider: { "@type": "Organization", name: "Instabridge Funding" },
                areaServed: { "@type": "City", name: `${city.name}, Ohio` },
                description: `Pre-settlement funding, lawsuit loans, and settlement advances for ${city.name} plaintiffs with active personal-injury lawsuits. $500–$25,000, funded in 24 hours. Non-recourse.`,
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
                  { "@type": "ListItem", position: 2, name: "States", item: `${SITE_URL}/states` },
                  { "@type": "ListItem", position: 3, name: "Ohio", item: `${SITE_URL}/states/ohio` },
                  { "@type": "ListItem", position: 4, name: city.name, item: `${SITE_URL}/states/ohio/${city.slug}` },
                ],
              },
            ],
          },
        }
      : { suffix: false, noindex: true },
  );

  if (!city) return <NotFound />;

  return (
    <div className="min-h-screen bg-white" data-testid={`city-page-${city.slug}`}>
      <Navbar />

      <div className="border-b border-[#E5E7EB] py-3">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 text-xs text-[#6B7280]">
          <Link to="/" className="hover:text-[#0A2540]">Home</Link> <span className="mx-2">/</span>
          <Link to="/states" className="hover:text-[#0A2540]">States</Link> <span className="mx-2">/</span>
          <Link to="/states/ohio" className="hover:text-[#0A2540]">Ohio</Link> <span className="mx-2">/</span>
          <span className="text-[#0A2540]">{city.name}</span>
        </div>
      </div>

      <section className="bg-[#F9FAFB] py-16 lg:py-20 border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="uppercase-label mb-5 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {city.name}, Ohio</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-light leading-[1.05]">
            <span className="font-semibold">{city.name} pre-settlement funding</span><br />&amp; lawsuit loans.
          </h1>
          <p className="mt-5 text-lg text-[#374151] leading-relaxed max-w-3xl font-medium">
            $500–$25,000 against your active injury lawsuit. Funded in 24 hours. Non-recourse — repay only if you win.
          </p>
          <p className="mt-4 text-base text-[#6B7280] leading-relaxed max-w-3xl">{city.intro}</p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/apply"><Button className="btn-primary-navy rounded-full h-12 px-6" data-testid={`city-${city.slug}-apply-btn`}>Apply for {city.name} funding <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-10">
            <div>
              <div className="uppercase-label mb-3">Common case types in {city.name}</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">Cases we frequently fund here.</h2>
              <ul className="mt-6 space-y-3">
                {city.common_cases.map((c) => (
                  <li key={c} className="flex items-start gap-3 p-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg">
                    <Scale className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-1" />
                    <span className="text-[#374151]">{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="uppercase-label mb-3">Local courts & landmarks</div>
              <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">Where {city.name} cases are heard.</h2>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {city.landmarks.map((l) => (
                  <div key={l} className="flex items-center gap-3 p-4 bg-white border border-[#E5E7EB] rounded-lg">
                    <Building2 className="w-4 h-4 text-[#0066FF] flex-shrink-0" />
                    <span className="text-sm text-[#374151]">{l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-[#0A2540] text-white rounded-2xl p-7 sticky top-24">
              <div className="uppercase-label !text-white/60 mb-3">Quick facts</div>
              <h3 className="font-display text-2xl font-semibold">{city.name}, Ohio</h3>
              <dl className="mt-6 space-y-4 text-sm">
                <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Population</dt><dd className="font-medium">{city.population}</dd></div>
                <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Court</dt><dd className="font-medium text-right">{city.courts}</dd></div>
                <div className="flex justify-between"><dt className="text-white/60">State</dt><dd className="font-medium">Ohio</dd></div>
              </dl>
              <Link to="/apply" className="block mt-7">
                <Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-11 w-full">Apply now</Button>
              </Link>
              <Link to="/states/ohio" className="block text-center text-sm text-white/60 hover:text-white mt-4">← Back to Ohio</Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Accident types we fund in this city */}
      <section className="py-16 bg-white border-t border-[#E5E7EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="uppercase-label mb-3">Case-type resources</div>
          <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">
            {city.name} case-type deep dives.
          </h2>
          <p className="mt-3 text-sm text-[#6B7280] max-w-2xl">
            Each accident-type page covers the applicable Ohio statute, the evidence that strengthens the case, typical settlement ranges, and city-specific funding notes.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ACCIDENTS.map((a) => (
              <Link key={a.slug} to={`/accidents/${a.slug}`}
                    data-testid={`city-${city.slug}-accident-${a.slug}`}
                    className="group bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#0066FF] rounded-lg p-4 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold text-[#0A2540]">{a.label}</div>
                  <ArrowRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#0066FF]" />
                </div>
                <div className="text-xs text-[#6B7280] mt-1">{a.plural} in {city.name}</div>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-4 text-sm">
            <Link to="/faq" className="text-[#0066FF] hover:underline font-medium flex items-center gap-1">
              <HelpCircle className="w-3.5 h-3.5" /> {city.name} funding FAQ
            </Link>
            <Link to="/how-it-works" className="text-[#0066FF] hover:underline font-medium">How our funding works</Link>
            <Link to="/tools/funding-cost-calculator" className="text-[#0066FF] hover:underline font-medium">Estimate funding cost</Link>
            <Link to="/services/pre-settlement" className="text-[#0066FF] hover:underline font-medium">Pre-settlement funding overview</Link>
          </div>
        </div>
      </section>

      {/* Other Ohio cities */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight">Other Ohio cities we fund</h2>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {OHIO_CITIES.filter((c) => c.slug !== city.slug).map((c) => (
              <Link key={c.slug} to={`/states/ohio/${c.slug}`} className="bg-white border border-[#E5E7EB] rounded-xl px-5 py-4 hover:border-[#0066FF] transition-colors">
                <div className="font-display font-semibold text-[#0A2540]">{c.name}</div>
                <div className="text-xs text-[#6B7280] mt-1">Pre-settlement loans →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
